import {
  SubmitErrorHandler,
  UseFormProps,
  UseFormReturn,
} from 'react-hook-form/dist/types';
import { SubmitHandler } from 'react-hook-form/dist/types/form';
import { FieldValues, useForm } from 'react-hook-form';
import { NavigateFunction } from 'react-router';
import { useErrorHandler } from './useErrorHandler';
import { useResetFormWhenDataIsLoaded } from './react-hook-form-helper';
import * as React from 'react';
import { useRef } from 'react';

type AdvancedFormReturnType<
  TFieldValues extends FieldValues = FieldValues,
  // eslint-disable-next-line @typescript-eslint/ban-types
  TContext extends object = object,
> = UseFormReturn<TFieldValues, TContext> & {
  overallError: string;
  formErrorCombined: string;
  handleSubmitDefault: (e?: React.BaseSyntheticEvent) => Promise<void>;
};

export function useAdvancedForm<
  TFieldValues extends FieldValues = FieldValues,
  // eslint-disable-next-line @typescript-eslint/ban-types
  TContext extends object = object,
>(
  submitHandler: (
    data: TFieldValues,
    history: NavigateFunction,
  ) => Promise<void>,
  defaultData?: TFieldValues | undefined,
  options?: {
    shouldResetOnSuccess?: boolean;
    initialize?: (form: UseFormReturn<TFieldValues, TContext>) => void;
  } & UseFormProps<TFieldValues, TContext>,
): AdvancedFormReturnType<TFieldValues, TContext> {
  const form = useForm<TFieldValues, TContext>(options);
  const isSubmitting = useRef(false);
  const shouldResetOnSuccess = options?.shouldResetOnSuccess ? true : false;
  const handler = useErrorHandler<TFieldValues>(
    submitHandler,
    form.setError,
    shouldResetOnSuccess ? (form.reset as any) : undefined,
  );
  options?.initialize?.(form);
  useResetFormWhenDataIsLoaded(form, defaultData);

  return {
    ...form,
    handleSubmit: (
      onValid: SubmitHandler<TFieldValues>,
      onInvalid: SubmitErrorHandler<TFieldValues> | undefined,
    ) => {
      return form.handleSubmit(async (values: TFieldValues) => {
        if (form.formState.isSubmitting) return;
        if (isSubmitting.current) return;
        isSubmitting.current = true;
        try {
          await onValid(values);
          return await handler.handler(values as any);
        } finally {
          isSubmitting.current = false;
        }
      }, onInvalid);
    },
    overallError: handler.overallServerError,
    formErrorCombined: handler.serverErrorsCombined,
    handleSubmitDefault: form.handleSubmit(async (values: TFieldValues) => {
      return await handler.handler(values as any);
    }),
  };
}
